import { FC, useEffect, useState } from 'react';
import { Form, Input, InputNumber, Modal, Select, Switch } from 'antd';
import {
  createInvoicingProduct,
  updateInvoicingProduct
} from 'services/api/invoicingProducts';
import { INVOICE_SERVICE_TYPE_OPTIONS } from 'constants/invoicing';
import {
  InvoiceServiceType,
  InvoicingProduct,
  ProductInput
} from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  open: boolean;
  product: InvoicingProduct | null; // null → create
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  nameEn: string;
  nameDe: string;
  serviceType: InvoiceServiceType;
  defaultCost: number;
  currency: string;
  defaultTaxRate: number;
  active: boolean;
};

const ProductModal: FC<IProps> = ({ open, product, onClose, onSuccess }) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const isEdit = !!product;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (product) {
      form.setFieldsValue({
        nameEn: product.nameEn,
        nameDe: product.nameDe,
        serviceType: product.serviceType,
        defaultCost: Number(product.defaultCost),
        currency: product.currency,
        defaultTaxRate: Number(product.defaultTaxRate),
        active: product.active
      });
    } else {
      form.resetFields();
    }
  }, [open, product, form]);

  const onFinish = async (values: FormValues) => {
    const input: ProductInput = {
      nameEn: values.nameEn.trim(),
      nameDe: values.nameDe.trim(),
      serviceType: values.serviceType,
      defaultCost: values.defaultCost,
      currency: (values.currency || 'EUR').toUpperCase(),
      defaultTaxRate: values.defaultTaxRate ?? 0,
      active: values.active
    };

    try {
      setLoading(true);

      if (product) {
        await updateInvoicingProduct(product.publicId, input);
      } else {
        await createInvoicingProduct(input);
      }

      onSuccess();
      onClose();
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? 'Save' : 'Create'}
      title={isEdit ? 'Edit product' : 'New product'}
      confirmLoading={loading}
      closable={!loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ currency: 'EUR', defaultTaxRate: 0, active: true }}
      >
        <Form.Item label="Name (EN)" name="nameEn" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Name (DE)" name="nameDe" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Service type"
          name="serviceType"
          rules={[{ required: true }]}
        >
          <Select options={INVOICE_SERVICE_TYPE_OPTIONS} />
        </Form.Item>
        <Form.Item
          label="Default cost"
          name="defaultCost"
          rules={[{ required: true }]}
        >
          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item
          label="Currency"
          name="currency"
          rules={[{ required: true }]}
        >
          <Input maxLength={3} />
        </Form.Item>
        <Form.Item label="Default tax rate (%)" name="defaultTaxRate">
          <InputNumber
            min={0}
            max={100}
            precision={2}
            style={{ width: '100%' }}
          />
        </Form.Item>
        {isEdit && (
          <Form.Item label="Active" name="active" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default ProductModal;
